/**
 * Locally synthesized sound. Nothing is streamed or downloaded.
 * Audio stays off until the user enables it.
 */
export class AudioController {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private ambient: GainNode | null = null;
  private noise: AudioBufferSourceNode | null = null;
  private enabled = false;
  private volume = 0.6;
  private captureDest: MediaStreamAudioDestinationNode | null = null;

  async setEnabled(enabled: boolean): Promise<void> {
    this.enabled = enabled;
    if (!enabled) {
      this.stopAmbient();
      return;
    }
    await this.ensure();
    this.startAmbient();
  }

  setVolume(percent: number): void {
    this.volume = percent / 100;
    if (this.master) this.master.gain.value = this.volume;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  captureStream(): MediaStream | null {
    if (!this.enabled || !this.ctx || !this.master) return null;
    if (!this.captureDest) {
      this.captureDest = this.ctx.createMediaStreamDestination();
      this.master.connect(this.captureDest);
    }
    return this.captureDest.stream;
  }

  supernova(): void {
    if (!this.enabled || !this.ctx || !this.master) return;
    this.burst(80, 0.4, 0.35);
    this.noiseHit(0.28);
  }

  blackHole(on: boolean): void {
    if (!this.enabled || !this.ctx || !this.master) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = on ? 42 : 70;
    gain.gain.value = 0.0001;
    osc.connect(gain).connect(this.master);
    osc.start();
    gain.gain.exponentialRampToValueAtTime(on ? 0.08 : 0.0001, this.ctx.currentTime + 0.4);
    osc.stop(this.ctx.currentTime + 1.2);
  }

  formationSweep(): void {
    if (!this.enabled || !this.ctx || !this.master) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(240, this.ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(720, this.ctx.currentTime + 0.35);
    gain.gain.value = 0.04;
    gain.gain.exponentialRampToValueAtTime(0.0001, this.ctx.currentTime + 0.4);
    osc.connect(gain).connect(this.master);
    osc.start();
    osc.stop(this.ctx.currentTime + 0.42);
  }

  dispose(): void {
    this.stopAmbient();
    void this.ctx?.close();
    this.ctx = null;
    this.master = null;
  }

  private async ensure(): Promise<void> {
    if (!this.ctx) {
      this.ctx = new AudioContext();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.volume;
      this.master.connect(this.ctx.destination);
    }
    if (this.ctx.state === "suspended") await this.ctx.resume();
  }

  private startAmbient(): void {
    if (!this.ctx || !this.master || this.noise) return;
    const buffer = this.ctx.createBuffer(1, this.ctx.sampleRate * 2, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) data[i] = (Math.random() * 2 - 1) * 0.18;
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    const filter = this.ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 420;
    const drone = this.ctx.createOscillator();
    drone.type = "sine";
    drone.frequency.value = 72;
    const droneGain = this.ctx.createGain();
    droneGain.gain.value = 0.03;
    this.ambient = this.ctx.createGain();
    this.ambient.gain.value = 0.22;
    src.connect(filter).connect(this.ambient);
    drone.connect(droneGain).connect(this.ambient);
    this.ambient.connect(this.master);
    src.start();
    drone.start();
    this.noise = src;
  }

  private stopAmbient(): void {
    try {
      this.noise?.stop();
    } catch {
      /* already stopped */
    }
    this.noise = null;
    this.ambient?.disconnect();
    this.ambient = null;
  }

  private burst(freq: number, duration: number, gainValue: number): void {
    if (!this.ctx || !this.master) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.frequency.setValueAtTime(freq, this.ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(freq * 0.4, this.ctx.currentTime + duration);
    gain.gain.value = gainValue;
    gain.gain.exponentialRampToValueAtTime(0.0001, this.ctx.currentTime + duration);
    osc.connect(gain).connect(this.master);
    osc.start();
    osc.stop(this.ctx.currentTime + duration + 0.05);
  }

  private noiseHit(duration: number): void {
    if (!this.ctx || !this.master) return;
    const buffer = this.ctx.createBuffer(1, this.ctx.sampleRate * duration, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i += 1)
      data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    const filter = this.ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 900;
    const gain = this.ctx.createGain();
    gain.gain.value = 0.12;
    src.connect(filter).connect(gain).connect(this.master);
    src.start();
  }
}
